import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { VOICE_DRAG_TYPE } from "@/utils/voiceDrag";

/** Keep casting sources and targets on the same row, including narrow windows. */
export default function VoiceAssignmentWorkspace({ children, hasCatalog, draggingVoice }: {
  children: ReactNode; hasCatalog: boolean; draggingVoice: boolean;
}) {
  const viewport = useRef<HTMLDivElement>(null);
  const columns = useRef<HTMLDivElement>(null);
  const topScrollbar = useRef<HTMLDivElement>(null);
  const dragPosition = useRef<{ x: number; y: number } | null>(null);
  const [contentWidth, setContentWidth] = useState(0);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const view = viewport.current, content = columns.current;
    if (!view || !content) return;
    const measure = () => {
      setContentWidth(view.scrollWidth);
      setOverflowing(view.scrollWidth > view.clientWidth + 1);
      if (topScrollbar.current) topScrollbar.current.scrollLeft = view.scrollLeft;
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(view); observer.observe(content);
    return () => observer.disconnect();
  }, [hasCatalog]);

  useEffect(() => {
    if (overflowing && topScrollbar.current && viewport.current)
      topScrollbar.current.scrollLeft = viewport.current.scrollLeft;
  }, [overflowing, contentWidth]);

  useEffect(() => {
    dragPosition.current = null;
    if (!draggingVoice) return;
    let frame: number;
    let previousTime = 0;
    const pan = (time: number) => {
      const view = viewport.current;
      const elapsed = Math.min(previousTime ? time - previousTime : 16, 32);
      previousTime = time;
      if (view && dragPosition.current !== null) {
        const rect = view.getBoundingClientRect();
        // The element can extend past the browser edge; use the visible bounds.
        const left = Math.max(0, rect.left), right = Math.min(window.innerWidth, rect.right);
        const edge = Math.max(1, Math.min(64, (right - left) / 4));
        const { x, y } = dragPosition.current;
        const speed = x < left + edge ? -Math.min(1, (left + edge - x) / edge)
          : x > right - edge ? Math.min(1, (x - right + edge) / edge) : 0;
        view.scrollLeft += speed * elapsed * 0.65;

        // Pan the character list, not the whole page, while an actor is held
        // near its visible top/bottom edge. The heading stays outside this list.
        const characters = view.querySelector<HTMLElement>('.voice-assignment-characters');
        if (characters) {
          const list = characters.getBoundingClientRect();
          const top = Math.max(0, list.top), bottom = Math.min(window.innerHeight, list.bottom);
          if (bottom > top && x >= Math.max(0, list.left) && x <= Math.min(window.innerWidth, list.right)
            && y >= top && y <= bottom) {
            const edgeY = Math.max(1, Math.min(64, (bottom - top) / 4));
            const speedY = y < top + edgeY ? -Math.min(1, (top + edgeY - y) / edgeY)
              : y > bottom - edgeY ? Math.min(1, (y - bottom + edgeY) / edgeY) : 0;
            characters.scrollTop += speedY * elapsed * 0.65;
          }
        }
      }
      frame = requestAnimationFrame(pan);
    };
    frame = requestAnimationFrame(pan);
    return () => cancelAnimationFrame(frame);
  }, [draggingVoice]);

  const navigateColumns = (event: KeyboardEvent<HTMLDivElement>) => {
    const view = viewport.current;
    // Leave nested filter/actor keyboard navigation unchanged.
    if (!view || event.target !== event.currentTarget) return;
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? view.scrollWidth - view.clientWidth
      : event.key === 'ArrowLeft' ? view.scrollLeft - 80 : event.key === 'ArrowRight' ? view.scrollLeft + 80 : null;
    if (next === null) return;
    event.preventDefault(); view.scrollLeft = next;
  };

  return <div className="min-w-0 max-w-full">
    {/* A second native scrollbar stays beside the heading even for long scripts. */}
    <div ref={topScrollbar} hidden={!overflowing} tabIndex={overflowing ? 0 : -1}
      role="region" aria-label="Scroll voice assignment columns"
      onKeyDown={navigateColumns}
      className="voice-assignment-scroll mb-3 overflow-x-auto overflow-y-hidden rounded bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onScroll={event => {
        if (viewport.current && viewport.current.scrollLeft !== event.currentTarget.scrollLeft)
          viewport.current.scrollLeft = event.currentTarget.scrollLeft;
      }}>
      <div aria-hidden="true" style={{ width: contentWidth, height: 1 }} />
    </div>
    <div ref={viewport} role="region" aria-label="Voice assignment workspace" tabIndex={0}
      onKeyDown={navigateColumns}
      className="voice-assignment-scroll max-w-full overflow-x-auto rounded pb-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onScroll={event => {
        if (topScrollbar.current && topScrollbar.current.scrollLeft !== event.currentTarget.scrollLeft)
          topScrollbar.current.scrollLeft = event.currentTarget.scrollLeft;
      }}
      onDragOverCapture={event => {
        if (!draggingVoice || !event.dataTransfer.types.includes(VOICE_DRAG_TYPE)) {
          dragPosition.current = null; return;
        }
        event.preventDefault(); dragPosition.current = { x: event.clientX, y: event.clientY };
      }}
      onDragLeave={event => {
        if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) dragPosition.current = null;
      }}
      onDropCapture={() => { dragPosition.current = null; }}
      onDragEndCapture={() => { dragPosition.current = null; }}>
      <div ref={columns} className={`voice-assignment-columns grid items-start gap-6 ${hasCatalog ? "min-w-[64rem] grid-cols-[minmax(0,1.4fr)_minmax(18rem,0.8fr)]" : ""}`}>
        {children}
      </div>
    </div>
  </div>;
}
